import { formatDateTime, formatMonth, formatTime, fromApi } from '@/utils/datetime';
import type { OnlineOrder } from '@restaurant/api-client';

/**
 * Current-locale timing wording (`t.orders.timing`), passed in because these are
 * pure utils called from within a `.map` (OrderCard is invoked as a function),
 * where a `useT` hook can't run - the parent resolves it once and hands it down.
 */
export interface OrderTimingLabels {
  asap: string;
  labelScheduled: string;
  labelReadyBy: string;
  /** Delivery counterpart of labelReadyBy: estimatedReadyAt is the handoff
   *  (delivered) instant for delivery orders, not a kitchen-ready time. */
  labelDeliveredBy: string;
  labelPlaced: string;
  dueNow: string;
  inMin: (m: number) => string;
  inHrMin: (h: number, m: number) => string;
  minOverdue: (m: number) => string;
  hrMinOverdue: (h: number, m: number) => string;
  placedAgo: (ago: string) => string;
  justNow: string;
  minAgo: (m: number) => string;
  hrMinAgo: (h: number, m: number) => string;
  chipScheduled: string;
  chipReady: string;
  chipOverdue: string;
  shortNow: string;
  shortMin: (m: number) => string;
  shortHr: (h: number) => string;
}

/** "in 25 min" / "due now" / "12 min overdue" for a due time staff must hit. */
function dueIn(iso: string, tl: OrderTimingLabels): { text: string; overdue: boolean } {
  const mins = Math.round(fromApi(iso).diffNow('minutes').minutes);
  if (mins >= 0) {
    if (mins < 1) return { text: tl.dueNow, overdue: false };
    if (mins < 60) return { text: tl.inMin(mins), overdue: false };
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return { text: tl.inHrMin(h, m), overdue: false };
  }
  const past = -mins;
  if (past < 60) return { text: tl.minOverdue(past), overdue: true };
  const h = Math.floor(past / 60);
  const m = past % 60;
  return { text: tl.hrMinOverdue(h, m), overdue: true };
}

/**
 * The single instant an order "lands" - the moment its work is due - as an ISO
 * string, using the same precedence orderTiming shows (scheduled slot → ready
 * ETA → placed). Buckets the by-hour load chart so each bar agrees with the time
 * on the card. The caller decides how to treat cancelled orders (they never
 * happened, so they're excluded from load).
 */
export function orderWhenIso(order: OnlineOrder): string {
  return order.scheduledFor ?? order.estimatedReadyAt ?? order.createdAt;
}

/**
 * The delivery leg in whole minutes (estimatedReadyAt − driverLeaveAt: handoff
 * minus food-ready). Null for pickup, or before the order's timing is stamped.
 * Shown next to the distance so the owner reads "how far" and "how long" together.
 */
export function deliveryTravelMinutes(order: OnlineOrder): number | null {
  if (order.type !== 'DELIVERY' || !order.driverLeaveAt || !order.estimatedReadyAt) return null;
  const mins = Math.round(
    (fromApi(order.estimatedReadyAt).toMillis() - fromApi(order.driverLeaveAt).toMillis()) / 60000,
  );
  return mins > 0 ? mins : null;
}

/** Same fallback the backend uses when no shift/holiday prep time resolves. */
export const DEFAULT_PREP_MINUTES = 15;

/** Whole minutes between two API timestamps (never negative). */
function minutesBetween(fromIso: string, toIso: string): number {
  return Math.max(0, Math.round((fromApi(toIso).toMillis() - fromApi(fromIso).toMillis()) / 60000));
}

/**
 * The prep leg the accept sheet proposes: the kitchen lead the backend stamped
 * at placement, or the flat fallback when it stamped none. The sheet seeds its
 * stepper from this and the analytics payload measures the operator's change
 * against it, so the two can't drift apart.
 */
export function seededPrepMinutes(order: OnlineOrder): number {
  return order.prepStartAt && order.driverLeaveAt
    ? minutesBetween(order.prepStartAt, order.driverLeaveAt)
    : DEFAULT_PREP_MINUTES;
}

/** "8 min ago" / "1h 5m ago" - how long an ASAP order has been waiting. */
function placedAgo(iso: string, tl: OrderTimingLabels): string {
  const mins = Math.round(-fromApi(iso).diffNow('minutes').minutes);
  if (mins < 1) return tl.justNow;
  if (mins < 60) return tl.minAgo(mins);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return tl.hrMinAgo(h, m);
}

/** Compact age for the narrow list-card column: "8m" / "1h". */
function shortAgo(iso: string, tl: OrderTimingLabels): string {
  const mins = Math.round(-fromApi(iso).diffNow('minutes').minutes);
  if (mins < 1) return tl.shortNow;
  if (mins < 60) return tl.shortMin(mins);
  return tl.shortHr(Math.floor(mins / 60));
}

/**
 * THE single timing decision for an order. Both the list card and the detail
 * hero call this - the branch (ASAP vs Ready-by vs Scheduled vs done) is made
 * exactly once, so the two screens can never disagree again.
 *
 * - `headline` is the dominant value (a clock time, or the word "ASAP").
 * - `label` is the short kind name ("Scheduled" / "Ready by" / "ASAP" /
 *   "Placed") - used by the hero.
 * - `detail` is the full relative phrase ("in 25 min", "placed 8 min ago") -
 *   used by the hero.
 * - `chip` is the compact form ("scheduled" / "ready" / "8m" / "overdue") -
 *   used by the card's tiny column.
 * - `tone` drives colour in both: overdue = red, muted = finished, normal.
 */
export function orderTiming(
  order: OnlineOrder,
  tl: OrderTimingLabels,
): {
  headline: string;
  headlineIsWord: boolean;
  label: string;
  detail: string;
  chip: string;
  tone: 'normal' | 'overdue' | 'muted';
} {
  // estimatedReadyAt is the handoff instant: kitchen-ready for pickup, but the
  // DELIVERED time for delivery (it already includes the travel leg). Label it
  // accordingly so a delivery order reads "Delivered by", not "Ready by".
  const readyLabel = order.type === 'DELIVERY' ? tl.labelDeliveredBy : tl.labelReadyBy;

  // Terminal states (cancelled or completed): keep the SAME meaningful time the
  // order showed while live (scheduled slot → ready ETA → placed) instead of
  // reverting to the placed time - a scheduled 21:30 delivery must not flip to
  // its 08:27 order time once it's cancelled or done.
  if (order.status === 'CANCELLED' || order.status === 'COMPLETED') {
    const when = order.scheduledFor ?? order.estimatedReadyAt ?? order.createdAt;
    const label = order.scheduledFor
      ? tl.labelScheduled
      : order.estimatedReadyAt
        ? readyLabel
        : tl.labelPlaced;
    return {
      headline: formatTime(when),
      headlineIsWord: false,
      label,
      detail: formatDateTime(when),
      chip: formatMonth(order.createdAt),
      tone: 'muted',
    };
  }

  if (order.scheduledFor) {
    const d = dueIn(order.scheduledFor, tl);
    return {
      headline: formatTime(order.scheduledFor),
      headlineIsWord: false,
      label: tl.labelScheduled,
      detail: d.text,
      chip: d.overdue ? tl.chipOverdue : tl.chipScheduled,
      tone: d.overdue ? 'overdue' : 'normal',
    };
  }

  // Ready-by ETA — only firm once accepted. Accept recomputes it (fresh now +
  // re-routed travel), so a PENDING ASAP order's ETA jumps on confirm; showing
  // it reads as a promise the kitchen then misses. While PENDING + ASAP, fall
  // through to the ASAP branch ("ASAP" + how long it's waited) instead. Scheduled
  // orders (handled above) stay on their stable time regardless of status.
  if (order.estimatedReadyAt && order.status !== 'PENDING') {
    const d = dueIn(order.estimatedReadyAt, tl);
    return {
      headline: formatTime(order.estimatedReadyAt),
      headlineIsWord: false,
      label: readyLabel,
      detail: d.text,
      chip: d.overdue ? tl.chipOverdue : tl.chipReady,
      tone: d.overdue ? 'overdue' : 'normal',
    };
  }

  return {
    headline: tl.asap,
    headlineIsWord: true,
    label: tl.asap,
    detail: tl.placedAgo(placedAgo(order.createdAt, tl)),
    chip: shortAgo(order.createdAt, tl),
    tone: 'normal',
  };
}
